import { Tab } from "@mui/base/Tab";
import { TabPanel } from "@mui/base/TabPanel";
import { Tabs } from "@mui/base/Tabs";
import { TabsList } from "@mui/base/TabsList";
import clsx from "clsx";
import { useState } from "react";

import { PaymentsSubscriptionCreateAttributes } from "~/api/Api";

import { Subscription } from "../../api/types";
import { sortByPrice } from "../../helpers/helpers";
import labels from "../../helpers/labels";
import { Collapsible } from "../ui/collapsible";
import styles from "./Pricing.module.scss";
import { PricingCard } from "./components/PricingCard";
import { usePricingPlans } from "./hooks/usePricingPlans";
import PlanTable from "./planComparison/PlanTable";
import { Product } from "./types";

type PricingProps = {
  products: Product[];
  subscription?: Subscription;
  mutate: (data: Omit<PaymentsSubscriptionCreateAttributes, "ua">) => void;
};

export const Pricing = ({ products, subscription, mutate }: PricingProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const subscriptionAddons =
    subscription?.["payments/subscription"]?.subscription_addons;
  //@ts-expect-error replase wrong type from API
  const plans: Plan[] = usePricingPlans(products, subscriptionAddons);
  const subsctiptionPriceId =
    subscription?.["payments/subscription"]?.price?.id;
  const annualPlans =
    plans?.filter((product) => product?.interval === "year") || [];
  const monthlyPlans =
    plans?.filter((product) => product?.interval === "month") || [];

  // Platform catalogs (e.g. Autopto) may have monthly-only plans, or a
  // single plan: hide the interval switcher and the comparison table when
  // there is nothing to switch to or compare with.
  const showTabs = monthlyPlans.length > 0 && annualPlans.length > 0;

  const renderPanel = (panelPlans: typeof monthlyPlans) => (
    <>
      <div className={styles.plans}>
        <div
          className={clsx(
            styles.plansInner,
            panelPlans.length === 1 && styles.plansSingle,
            panelPlans.length === 2 && styles.plansPair,
          )}
        >
          {sortByPrice(panelPlans).map((plan) => (
            <PricingCard
              key={plan.id}
              //@ts-expect-error replase wrong type from API
              plan={plan}
              mutate={mutate}
              subsctiptionPriceId={subsctiptionPriceId}
            />
          ))}
        </div>
      </div>
      {panelPlans.length > 1 && (
        <div>
          <div className={styles.collapsible}>
            <Collapsible onToggle={handleToggle} />
          </div>
          {isOpen && <PlanTable filteredPlans={panelPlans} />}
        </div>
      )}
    </>
  );

  if (!showTabs) {
    return renderPanel(monthlyPlans.length > 0 ? monthlyPlans : annualPlans);
  }

  return (
    <Tabs defaultValue={labels.MONTHLY_PAYMENT}>
      <TabsList className={styles.tabsList}>
        <Tab className={styles.tabButton} value={labels.MONTHLY_PAYMENT}>
          {labels.MONTHLY_PAYMENT}
        </Tab>
        <Tab className={styles.tabButton} value={labels.ANNUALLY_PAYMENT}>
          {labels.ANNUALLY_PAYMENT}
        </Tab>
      </TabsList>
      <TabPanel value={labels.MONTHLY_PAYMENT}>
        {renderPanel(monthlyPlans)}
      </TabPanel>
      <TabPanel value={labels.ANNUALLY_PAYMENT}>
        {renderPanel(annualPlans)}
      </TabPanel>
    </Tabs>
  );
};
